import { DateTime } from "luxon"
import { parseApiCalendarDate } from "./HelperDate"
import type { IOrderQuickFormatRowSnapshot } from "./InterfacesAndTypes"

export const QUICK_FORMAT_SECTION1_MIN_WIDTH = 40
export const QUICK_FORMAT_SECTION2_MIN_WIDTH = 15
export const QUICK_FORMAT_SECTION3_MIN_WIDTH = 11

export const QUICK_FORMAT_ALIAS_PAIR_SEPARATOR = "; "

export type QuickFormatMode = "email" | "fax" | "plain"

function normalizeForCompare(s: string): string {
	return s.trim().replace(/\s+/g, " ").toLowerCase()
}

function rowString(row: Record<string, unknown>, key: string): string {
	const v = row[key]
	return typeof v === "string" ? v : ""
}

export function parseAliasPairs(aliasRaw: string): { lastDisplay: string, firstDisplay: string }[] {
	const out: { lastDisplay: string, firstDisplay: string }[] = []
	for (const segment of aliasRaw.split(";")) {
		const trimmed = segment.trim()
		const commaIdx = trimmed.indexOf(",")
		if (commaIdx === -1) {
			continue
		}
		const lastDisplay = trimmed.slice(0, commaIdx).trim()
		const firstDisplay = trimmed.slice(commaIdx + 1).trim()
		if (!lastDisplay.length && !firstDisplay.length) {
			continue
		}
		out.push({ lastDisplay, firstDisplay })
	}
	return out
}

function collectDistinctAliasParts(
	pairs: { lastDisplay: string, firstDisplay: string }[],
	legalNorm: string,
	pick: "last" | "first",
): string[] {
	const seenNorm = new Set<string>()
	const ordered: string[] = []
	for (const p of pairs) {
		const display = pick === "last" ? p.lastDisplay : p.firstDisplay
		const norm = normalizeForCompare(display)
		if (!norm.length || norm === legalNorm) {
			continue
		}
		if (seenNorm.has(norm)) {
			continue
		}
		seenNorm.add(norm)
		ordered.push(display)
	}
	return ordered
}

export function buildQuickFormatSection1(lastName: string, firstName: string, aliasRaw: string): string {
	const legalLastNorm = normalizeForCompare(lastName)
	const legalFirstNorm = normalizeForCompare(firstName)
	const pairs = parseAliasPairs(aliasRaw)
	const lastAliases = collectDistinctAliasParts(pairs, legalLastNorm, "last")
	const firstAliases = collectDistinctAliasParts(pairs, legalFirstNorm, "first")
	let left = lastName
	if (lastAliases.length) {
		left += ` / ${lastAliases.join(QUICK_FORMAT_ALIAS_PAIR_SEPARATOR)}`
	}
	let right = firstName
	if (firstAliases.length) {
		right += ` / ${firstAliases.join(QUICK_FORMAT_ALIAS_PAIR_SEPARATOR)}`
	}
	return `${left}, ${right}`
}

export function padSection(s: string, minWidth: number): string {
	if (s.length >= minWidth) {
		return s
	}
	return s + " ".repeat(minWidth - s.length)
}

export function formatQuickFormatDob(dateOfBirth: string): string {
	if (!dateOfBirth?.trim()) {
		return ""
	}
	const trimmed = dateOfBirth.trim()
	const fromCal = parseApiCalendarDate(trimmed)
	if (fromCal) {
		return fromCal.toFormat("MM/dd/yyyy")
	}
	const dt = DateTime.fromISO(trimmed)
	if (dt.isValid) {
		return dt.toFormat("MM/dd/yyyy")
	}
	return trimmed
}

function buildQuickFormatRowLine(row: Record<string, unknown>, includeSsn: boolean): string {
	const lastName = rowString(row, "lastName")
	const firstName = rowString(row, "firstName")
	const aliasRaw = rowString(row, "alias")
	const section1 = buildQuickFormatSection1(lastName, firstName, aliasRaw)
	const dobPretty = formatQuickFormatDob(rowString(row, "dateOfBirth"))
	const section2Raw = `DOB: ${dobPretty}`
	const p1 = padSection(section1, QUICK_FORMAT_SECTION1_MIN_WIDTH)
	const p2 = padSection(section2Raw, QUICK_FORMAT_SECTION2_MIN_WIDTH)
	if (!includeSsn) {
		return `${p1}  ${p2}`
	}
	const ssn = rowString(row, "ssnLastFour")
	const section3Raw = ssn.length ? `SSN: #${ssn}` : ""
	const p3 = padSection(section3Raw, QUICK_FORMAT_SECTION3_MIN_WIDTH)
	return `${p1}  ${p2}  ${p3}`
}

export function buildQuickFormatEmailLine(row: Record<string, unknown>): string {
	return buildQuickFormatRowLine(row, true)
}

export function buildQuickFormatOutput(rows: IOrderQuickFormatRowSnapshot[], mode: QuickFormatMode): string {
	const includeSsn = mode !== "fax"
	return rows.map((snap) => buildQuickFormatRowLine(snap.row, includeSsn)).join("\n")
}
